import { Sodo } from '../shared/interfaces';
import { GameType } from '../shared/enume';

// Maximum run value for Session games
export const MAX_RUN_FOR_SESSION = 800;

interface PartyCommission {
  ThirdPartyId?: number | null;
  TestPartyCommission?: number | null;
  OneDayPartyCommission?: number | null;
  SessionPartyCommission?: number | null;
  TestThirdPartyCommission?: number | null;
  OneDayThirdPartyCommission?: number | null;
  SessionThirdPartyCommission?: number | null;
  HarCut?: number | null;
  ThirdPartyHarCut?: number | null;
  HarCutThirdPartyId?: number | null;
}

interface CommissionSnapshot {
  Partners?: any | null;
  PartyCommission?: PartyCommission | null;
}

/**
 * HarCut delta applied after base win/loss, before commission, inside {@link calculateSodaAmount}.
 * @returns Amount to add to the intermediate `amount` (0 when no HarCut applies).
 */
export const calculateHarcut = (sodo: Sodo, amount: number): number => {
  if (!sodo.commissionSnapshot) {
    return 0;
  }

  const commissionSnapshot = typeof sodo.commissionSnapshot === 'string' ? JSON.parse(sodo.commissionSnapshot) : sodo.commissionSnapshot;
  const partyCommission = commissionSnapshot.PartyCommission;
  const harCut = partyCommission?.HarCut ?? 0;

  if (amount <= 0) {
    return 0;
  }

  return (amount / 100000) * harCut;
};

/**
 * HarCut adjustment for partner sodas, subtracted from intermediate `amount` before commission in {@link calculateSodaAmount}.
 * @param amount - Base P&amp;L after LK/rate rules, before HarCut and commission
 * @returns Amount to subtract from `amount` (0 when no partner HarCut applies)
 */
export const calculateHarcutForPartnerSoda = (sodo: Sodo, amount: number): number => {
  if (!sodo.commissionSnapshot) {
    return 0;
  }

  const commissionSnapshot = typeof sodo.commissionSnapshot === 'string' ? JSON.parse(sodo.commissionSnapshot) : sodo.commissionSnapshot;
  const partyCommission = commissionSnapshot.PartyCommission;
  const harCut = partyCommission?.HarCut ?? 0;

  if (harCut === 0) {
    return 0;
  }

  if (amount >= 0) {
    return 0;
  }

  return (amount / 100000) * harCut;
};

/**
 * Third-party HarCut delta: same formula as {@link calculateHarcut} using `PartyCommission.ThirdPartyHarCut` from commissionSnapshot.
 * Applied only when the intermediate P&amp;L (before HarCuts) is positive, same as party HarCut.
 */
export const calculateThirdPartyHarcut = (sodo: Sodo, amount: number): number => {
  if (!sodo.commissionSnapshot) {
    return 0;
  }

  const commissionSnapshot = typeof sodo.commissionSnapshot === 'string' ? JSON.parse(sodo.commissionSnapshot) : sodo.commissionSnapshot;
  const partyCommission = commissionSnapshot.PartyCommission;
  const tph = partyCommission?.ThirdPartyHarCut ?? 0;

  if (amount <= 0 || !tph) {
    return 0;
  }

  return (amount / 100000) * tph;
};

/**
 * Third-party HarCut for partner sodas: same rules as {@link calculateHarcutForPartnerSoda} using `ThirdPartyHarCut`.
 */
export const calculateThirdPartyHarcutForPartnerSoda = (sodo: Sodo, amount: number): number => {
  if (!sodo.commissionSnapshot) {
    return 0;
  }

  const commissionSnapshot = typeof sodo.commissionSnapshot === 'string' ? JSON.parse(sodo.commissionSnapshot) : sodo.commissionSnapshot;
  const partyCommission = commissionSnapshot.PartyCommission;
  const tph = partyCommission?.ThirdPartyHarCut ?? 0;

  if (!tph || amount >= 0) {
    return 0;
  }

  return (amount / 100000) * tph;
};

/**
 * Parsed `ThirdPartyHarCut` rate from a soda commission snapshot (0 if missing).
 * Use for UI row/foot totals alongside party {@link HarCut}.
 */
export const getThirdPartyHarCutRateFromCommissionSnapshot = (
  commissionSnapshot: Sodo['commissionSnapshot'],
): number => {
  if (!commissionSnapshot) return 0;
  try {
    const snap = typeof commissionSnapshot === 'string' ? JSON.parse(commissionSnapshot) : commissionSnapshot;
    const raw = snap?.PartyCommission?.ThirdPartyHarCut;
    if (raw === null || raw === undefined || raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
};

/**
 * Calculates amount based on soda detail and team name
 * This is the EXACT logic from GameBoard.tsx getAmount function (lines 410-437)
 * 
 * Logic:
 * 1. If no LK or teamName, return amount
 * 2. If LK is just "L" (single letter) and team doesn't match: return -amount
 * 3. If LK is just "K" (single letter) and team doesn't match: return amount
 * 4. Otherwise (team matches OR compound code): check firstLetter
 *    - If firstLetter is 'K': return -(amount * rate) / 100
 *    - Otherwise: return (amount * rate) / 100
 * 
 * @param sodo - The complete soda object containing LK, amount, rate, and team
 * @param teamName - The team/country name we're calculating for
 * @returns The calculated amount based on the rules
 */
export const calculateSodaAmount = (sodo: Sodo, teamName: string): number => {
  // Match GameBoard line 411-412 exactly
  if (!sodo.LK || !teamName) {
    return sodo.amount; // Return full amount if no soda detail or team name
  }

  // Match GameBoard lines 415-418 exactly
  const sodaUpper = sodo.LK.toUpperCase();
  const firstLetter = sodaUpper.charAt(0);
  const teamUpper = teamName.toUpperCase();
  const sodaTeamUpper = sodo.team.toUpperCase();
  
  // Match GameBoard line 421 exactly
  // Check if the team for this initial matches the current team
  const isMatchingTeam = sodaTeamUpper === teamUpper;
  
  // Match GameBoard lines 424-427 exactly
  // If first letter doesn't correspond to this team, return full amount
  let amount: number;
  if (!isMatchingTeam && sodaUpper === 'L') {
    amount = -sodo.amount;
  } else if (!isMatchingTeam && sodaUpper === 'K') {
    amount = sodo.amount;
  } else {
    // Match GameBoard lines 430-436 exactly
    // If first letter matches this team, check the second letter
    // (Note: Comment says "second letter" but code checks firstLetter - matching GameBoard exactly)
    if (firstLetter === 'K') {
      // For 'K': matching team gets negative, opposite gets positive
      amount = -(sodo.amount * sodo.rate /100);
    } else {
      // For 'L': matching team gets positive, opposite gets negative
      amount = (sodo.amount * sodo.rate /100);
    }
  }

  /** P&amp;L after LK rules, before party/third-party HarCuts and commission (both HarCuts use this same base). */
  const preHarCutAmount = amount;

  // If soda is a partner soda, add commission; otherwise subtract commission
  const commission = calculateCommission(sodo);
  const thirdPartyCommission = calculateThirdPartyCommission(sodo);

  if (sodo.isPartnerSoda) {
    // For partner sodas, add commission to the amount
    amount = amount - calculateHarcutForPartnerSoda(sodo, preHarCutAmount);
    amount = amount - calculateThirdPartyHarcutForPartnerSoda(sodo, preHarCutAmount);
    amount = Number((amount + commission + thirdPartyCommission).toFixed(4));
  } else {
    amount = amount - calculateHarcut(sodo, preHarCutAmount);
    amount = amount - calculateThirdPartyHarcut(sodo, preHarCutAmount);
    // For regular sodas, subtract commission from the amount
    amount = Number((amount - commission - thirdPartyCommission).toFixed(4));
  }
  // Calculate and add commission to the amount
  amount = Number((amount * 1000 / 100).toFixed(2));
  return amount;
};

/**
 * Same win/loss rules as {@link calculateSodaAmount} but **without** party/third-party commission.
 * Use when the business rule must use the raw P&L scale (e.g. HarCut = soda amount × party harCut / 100000).
 */
export const calculateSodaAmountBeforeCommission = (sodo: Sodo, teamName: string): number => {
  if (!sodo.LK || !teamName) {
    return sodo.amount;
  }

  const sodaUpper = sodo.LK.toUpperCase();
  const firstLetter = sodaUpper.charAt(0);
  const teamUpper = teamName.toUpperCase();
  const sodaTeamUpper = sodo.team.toUpperCase();

  const isMatchingTeam = sodaTeamUpper === teamUpper;

  let amount: number;
  if (!isMatchingTeam && sodaUpper === 'L') {
    amount = -sodo.amount;
  } else if (!isMatchingTeam && sodaUpper === 'K') {
    amount = sodo.amount;
  } else {
    if (firstLetter === 'K') {
      amount = -(sodo.amount * sodo.rate) / 100;
    } else {
      amount = (sodo.amount * sodo.rate) / 100;
    }
  }

  return Number(((amount * 1000) / 100).toFixed(2));
};

/**
 * Calculates amount based on individual parameters (alternative to using Soda object)
 * This uses the same logic as calculateSodaAmount but accepts individual parameters
 * 
 * @param lk - The LK code (e.g., "A/L", "A/K", "L", "K")
 * @param amount - The amount value
 * @param rate - The rate value
 * @param transactionTeam - The team this transaction is for (from sodo.team)
 * @param teamName - The team/country name we're calculating for
 * @returns The calculated amount based on the rules
 */
export const calculateSodaAmountFromParams = (
  lk: string,
  amount: number,
  rate: number,
  transactionTeam: string,
  teamName: string
): number => {
  // Early return if no LK or teamName
  if (!lk || !teamName) {
    return amount;
  }

  const sodaUpper = lk.toUpperCase();
  const firstLetter = sodaUpper.charAt(0);
  const teamUpper = teamName.toUpperCase();
  const sodaTeamUpper = (transactionTeam || '').toUpperCase();

  // Check if the team for this transaction matches the current team
  const isMatchingTeam = sodaTeamUpper === teamUpper;

  // Handle single-letter LK codes when team doesn't match
  if (!isMatchingTeam && sodaUpper === 'L') {
    return -amount;
  } else if (!isMatchingTeam && sodaUpper === 'K') {
    return amount;
  }

  // Handle compound codes or matching teams
  if (firstLetter === 'K') {
    // For 'K': matching team gets negative, opposite gets positive
    return -(amount * rate) / 100;
  } else {
    // For 'L': matching team gets positive, opposite gets negative
    return (amount * rate) / 100;
  }
};

/**
 * Calculates commission for a soda based on commissionSnapshot and game type
 * Commission is per lakh, so amount is multiplied by 1000 to convert to lakhs
 * 
 * @param sodo - The soda object containing commissionSnapshot and gameType
 * @returns The commission amount, or 0 if commission is null or property not found
 */
export const calculateCommission = (sodo: Sodo): number => {
  // Check if commissionSnapshot exists
  if (!sodo.commissionSnapshot) {
    return 0;
  }

  // Parse commissionSnapshot if it's a string, otherwise use it as-is
  let commissionSnapshot: CommissionSnapshot;
  if (typeof sodo.commissionSnapshot === 'string') {
    try {
      commissionSnapshot = JSON.parse(sodo.commissionSnapshot);
    } catch (error) {
      console.error('Failed to parse commissionSnapshot:', error);
      return 0;
    }
  } else {
    commissionSnapshot = sodo.commissionSnapshot;
  }

  // Check if PartyCommission exists
  if (!commissionSnapshot.PartyCommission) {
    return 0;
  }

  const partyCommission = commissionSnapshot.PartyCommission;
  
  // Handle gameType with better error tracking
  const gameTypeRaw = sodo.gameType;
  const gameType = gameTypeRaw?.toLowerCase()?.trim();
  
  // If gameType is not set, we cannot determine which commission to use
  if (!gameType) {
    console.warn('gameType is not set on sodo object, cannot calculate commission', {
      sodoId: sodo.id,
      gameId: sodo.gameId,
      gameTypeRaw: gameTypeRaw,
      gameTypeValue: sodo.gameType,
      sodoKeys: Object.keys(sodo),
      sodoFull: sodo, // Full object for debugging
    });
    return 0;
  }

  // Determine which commission field to use based on game type
  let commissionRate: number | null | undefined = null;

  if (gameType === GameType.oneday) {
    commissionRate = partyCommission.OneDayPartyCommission;
  } else if (gameType === GameType.test) {
    commissionRate = partyCommission.TestPartyCommission;
  } else if (gameType === GameType.session) {
    commissionRate = partyCommission.SessionPartyCommission;
  }

  // If commission is null or undefined, return 0
  if (commissionRate === null || commissionRate === undefined) {
    return 0;
  }

  // Commission is per lakh, so:
  // 1. Multiply amount by 1000 (as per requirement)
  // 2. Calculate commission per lakh prorata: (amount * 1000) / 100000 * commissionRate
  // Example: amount = 200, commissionRate = 10
  // Commission = (200 * 1000) / 100000 * 10 = 200000 / 100000 * 10 = 2 * 10 = 20
  const amountMultiplied = sodo.amount * 1000;
  const amountInLakhs = amountMultiplied / 100000; // Convert to lakhs (1 lakh = 100,000)
  const commissionAmount = amountInLakhs * commissionRate;
  return Number((commissionAmount/1000).toFixed(4));
};

/**
 * Calculates third-party commission for a soda based on commissionSnapshot and game type
 * Commission is per lakh, so amount is multiplied by 1000 to convert to lakhs
 * Uses TestThirdPartyCommission, OneDayThirdPartyCommission, or SessionThirdPartyCommission
 * based on the game type
 * 
 * @param sodo - The soda object containing commissionSnapshot and gameType
 * @returns The third-party commission amount, or 0 if commission is null or property not found
 */
export const calculateThirdPartyCommission = (sodo: Sodo): number => {
  // Check if commissionSnapshot exists
  if (!sodo.commissionSnapshot) {
    return 0;
  }

  // Parse commissionSnapshot if it's a string, otherwise use it as-is
  let commissionSnapshot: CommissionSnapshot;
  if (typeof sodo.commissionSnapshot === 'string') {
    try {
      commissionSnapshot = JSON.parse(sodo.commissionSnapshot);
    } catch (error) {
      console.error('Failed to parse commissionSnapshot:', error);
      return 0;
    }
  } else {
    commissionSnapshot = sodo.commissionSnapshot;
  }

  // Check if PartyCommission exists
  if (!commissionSnapshot.PartyCommission) {
    return 0;
  }

  const partyCommission = commissionSnapshot.PartyCommission;
  
  // Handle gameType with better error tracking
  const gameTypeRaw = sodo.gameType;
  const gameType = gameTypeRaw?.toLowerCase()?.trim();
  
  // If gameType is not set, we cannot determine which commission to use
  if (!gameType) {
    console.warn('gameType is not set on sodo object, cannot calculate third-party commission', {
      sodoId: sodo.id,
      gameId: sodo.gameId,
      gameTypeRaw: gameTypeRaw,
      gameTypeValue: sodo.gameType,
      sodoKeys: Object.keys(sodo),
      sodoFull: sodo, // Full object for debugging
    });
    return 0;
  }

  // Determine which third-party commission field to use based on game type
  let commissionRate: number | null | undefined = null;

  if (gameType === GameType.oneday) {
    commissionRate = partyCommission.OneDayThirdPartyCommission;
  } else if (gameType === GameType.test) {
    commissionRate = partyCommission.TestThirdPartyCommission;
  } else if (gameType === GameType.session) {
    commissionRate = partyCommission.SessionThirdPartyCommission;
  }

  // If commission is null or undefined, return 0
  if (commissionRate === null || commissionRate === undefined) {
    return 0;
  }

  // Commission is per lakh, so:
  // 1. Multiply amount by 1000 (as per requirement)
  // 2. Calculate commission per lakh prorata: (amount * 1000) / 100000 * commissionRate
  // Example: amount = 200, commissionRate = 10
  // Commission = (200 * 1000) / 100000 * 10 = 200000 / 100000 * 10 = 2 * 10 = 20
  const amountMultiplied = sodo.amount * 1000;
  const amountInLakhs = amountMultiplied / 100000; // Convert to lakhs (1 lakh = 100,000)
  const commissionAmount = amountInLakhs * commissionRate;
  return Number((commissionAmount/1000).toFixed(4));
};

/**
 * Type definition for run-wise amounts
 * Maps run number (0-800) to win/loss amount
 */
export type RunWiseAmounts = Map<number, number>;

/**
 * Interface for simplified soda data used in run-wise calculations
 */
export interface RunWiseSodaData {
  runs: number;      // Run number (e.g., 50 from "50/L")
  lorK: 'L' | 'K';   // Type: 'L' or 'K'
  rate: number;      // Rate value
  amount: number;    // Amount value
}

/**
 * Parses LK string (e.g., "50/L", "56/K", "L", "K") into run number and type
 * 
 * @param lk - The LK string to parse (e.g., "50/L", "56/K", "L", "K", "50L", "50K")
 * @returns An object containing runNumber and type ('L' or 'K')
 */
export const parseLKToRunAndType = (lk: string): { runNumber: number; type: 'L' | 'K' } => {
  const lkUpper = (lk || '').toUpperCase().trim();
  let runNumber: number = 0;
  let type: 'L' | 'K' = 'L';
  
  if (lkUpper.includes('/')) {
    // Format: "50/L" or "50/K"
    const parts = lkUpper.split('/');
    const runPart = parts[0].trim();
    const typePart = parts[1]?.trim() || '';
    
    // Extract numeric part from run (handle cases like "50" or "50A")
    const numericMatch = runPart.match(/\d+/);
    runNumber = numericMatch ? parseInt(numericMatch[0], 10) : 0;
    
    // Determine type
    if (typePart === 'K' || lkUpper.includes('/K')) {
      type = 'K';
    } else {
      type = 'L';
    }
  } else if (lkUpper === 'L' || lkUpper === 'K') {
    // Single letter format - default to run 0
    runNumber = 0;
    type = lkUpper === 'K' ? 'K' : 'L';
  } else {
    // Try to extract number from LK (e.g., "50L" or "50K")
    const numericMatch = lkUpper.match(/\d+/);
    runNumber = numericMatch ? parseInt(numericMatch[0], 10) : 0;
    type = lkUpper.includes('K') ? 'K' : 'L';
  }
  
  return { runNumber, type };
};

/**
 * Calculates win/loss for each run (0 to MAX_RUN_FOR_SESSION) for a single soda
 * 
 * Logic:
 * - For "50/L" (L type):
 *   - Runs 50 to MAX_RUN_FOR_SESSION: positive (rate * amount * 1000)
 *   - Runs 0 to < 50: negative (amount * 1000)
 * 
 * - For "50/K" (K type):
 *   - Runs 0 to < 50: positive (rate * amount * 1000)
 *   - Runs 50 to MAX_RUN_FOR_SESSION: negative (amount * 1000)
 * 
 * @param sodaData - The soda data object containing runs, lorK, rate, and amount
 * @returns A Map where key is run number (0-800) and value is win/loss amount for that run
 */
export const calculateRunWiseWinLossForSoda = (sodaData: RunWiseSodaData): RunWiseAmounts => {
  const runWiseAmounts = new Map<number, number>();
  
  const { runs: runNumber, lorK: type, rate, amount } = sodaData;
  
  // Calculate amounts for each run from 0 to MAX_RUN_FOR_SESSION
  for (let run = 0; run <= MAX_RUN_FOR_SESSION; run++) {
    let winLossAmount = 0;
    
    if (type === 'L') {
      // For "50/L":
      // - Runs 50 to MAX_RUN_FOR_SESSION: positive (rate * amount * 1000)
      // - Runs 0 to < 50: negative (amount * 1000)
      if (run >= runNumber) {
        // Runs from runNumber to MAX_RUN_FOR_SESSION: positive
        winLossAmount = rate/100 * amount * 1000;
      } else {
        // Runs below runNumber: negative
        winLossAmount = -(amount * 1000);
      }
    } else {
      // For "50/K":
      // - Runs 0 to < 50: positive (rate * amount * 1000)
      // - Runs 50 to MAX_RUN_FOR_SESSION: negative (amount * 1000)
      if (run < runNumber) {
        winLossAmount = (amount * 1000);
        // Runs below runNumber: positive
        
      } else {
        // Runs from runNumber to MAX_RUN_FOR_SESSION: negative
        winLossAmount = -(rate/100 * amount * 1000);
      }
    }
    
    runWiseAmounts.set(run, winLossAmount);
  }
  
  return runWiseAmounts;
};

/**
 * Calculates party commission for Session games as a percentage
 * Commission is calculated as: (amount * 1000) * (commissionRate / 100)
 * 
 * @param sodo - The soda object containing commissionSnapshot and amount
 * @returns The party commission amount, or 0 if commission is null or property not found
 */
export const calculateSessionPartyCommission = (sodo: Sodo): number => {
  // Check if commissionSnapshot exists
  if (!sodo.commissionSnapshot) {
    return 0;
  }

  // Parse commissionSnapshot if it's a string, otherwise use it as-is
  let commissionSnapshot: CommissionSnapshot;
  if (typeof sodo.commissionSnapshot === 'string') {
    try {
      commissionSnapshot = JSON.parse(sodo.commissionSnapshot);
    } catch (error) {
      console.error('Failed to parse commissionSnapshot:', error);
      return 0;
    }
  } else {
    commissionSnapshot = sodo.commissionSnapshot;
  }

  // Check if PartyCommission exists
  if (!commissionSnapshot.PartyCommission) {
    return 0;
  }

  const partyCommission = commissionSnapshot.PartyCommission;
  
  // Get SessionPartyCommission (percentage)
  const commissionRate = partyCommission.SessionPartyCommission;

  // If commission is null or undefined, return 0
  if (commissionRate === null || commissionRate === undefined) {
    return 0;
  }

  // For Session games, commission is a percentage:
  // 1. Multiply amount by 1000 (as per requirement) to get actual amount
  // 2. Calculate commission as percentage: (amount * 1000) * (commissionRate / 100)
  // 3. Keep commission in the same scale as run-wise amounts (multiplied by 1000)
  // Example: amount = 200, commissionRate = 5 (meaning 5%)
  // Commission = (200 * 1000) * (5 / 100) = 200000 * 0.05 = 10000
  // This is kept in the multiplied scale to match run-wise amounts
  const amountMultiplied = sodo.amount * 1000;
  const commissionAmount = amountMultiplied * (commissionRate / 100);
  return Number(commissionAmount.toFixed(4)); // Keep in multiplied scale to match run-wise amounts
};

/**
 * Calculates third-party commission for Session games as a percentage
 * Commission is calculated as: (amount * 1000) * (commissionRate / 100)
 * 
 * @param sodo - The soda object containing commissionSnapshot and amount
 * @returns The third-party commission amount, or 0 if commission is null or property not found
 */
export const calculateSessionThirdPartyCommission = (sodo: Sodo): number => {
  // Check if commissionSnapshot exists
  if (!sodo.commissionSnapshot) {
    return 0;
  }

  // Parse commissionSnapshot if it's a string, otherwise use it as-is
  let commissionSnapshot: CommissionSnapshot;
  if (typeof sodo.commissionSnapshot === 'string') {
    try {
      commissionSnapshot = JSON.parse(sodo.commissionSnapshot);
    } catch (error) {
      console.error('Failed to parse commissionSnapshot:', error);
      return 0;
    }
  } else {
    commissionSnapshot = sodo.commissionSnapshot;
  }

  // Check if PartyCommission exists
  if (!commissionSnapshot.PartyCommission) {
    return 0;
  }

  const partyCommission = commissionSnapshot.PartyCommission;
  
  // Get SessionThirdPartyCommission (percentage)
  const commissionRate = partyCommission.SessionThirdPartyCommission;

  // If commission is null or undefined, return 0
  if (commissionRate === null || commissionRate === undefined) {
    return 0;
  }

  // For Session games, commission is a percentage:
  // 1. Multiply amount by 1000 (as per requirement) to get actual amount
  // 2. Calculate commission as percentage: (amount * 1000) * (commissionRate / 100)
  // 3. Keep commission in the same scale as run-wise amounts (multiplied by 1000)
  // Example: amount = 200, commissionRate = 3 (meaning 3%)
  // Commission = (200 * 1000) * (3 / 100) = 200000 * 0.03 = 6000
  // This is kept in the multiplied scale to match run-wise amounts
  const amountMultiplied = sodo.amount * 1000;
  const commissionAmount = amountMultiplied * (commissionRate / 100);
  return Number(commissionAmount.toFixed(4)); // Keep in multiplied scale to match run-wise amounts
};

/**
 * Calculates total win/loss for each run (0 to MAX_RUN_FOR_SESSION) across all sodas
 * This function loops through all sodas and accumulates the run-wise amounts
 * For Session games, commission is calculated once per soda and deducted from all run-wise amounts
 * 
 * @param sodas - Array of soda objects
 * @param isSessionGame - Whether this is a Session game (default: false)
 * @returns A Map where key is run number (0-800) and value is total win/loss amount for that run
 */
export const calculateTotalRunWiseWinLoss = (sodas: Sodo[], isSessionGame: boolean = true): RunWiseAmounts => {
  const totalRunWiseAmounts = new Map<number, number>();
  
  // Initialize all runs to 0
  for (let run = 0; run <= MAX_RUN_FOR_SESSION; run++) {
    totalRunWiseAmounts.set(run, 0);
  }
  
  // Loop through each soda and accumulate amounts
  for (const sodo of sodas) {
    // Parse LK to extract run number and type
    const { runNumber, type } = parseLKToRunAndType(sodo.LK || '');
    
    // Create simplified soda data object
    const sodaData: RunWiseSodaData = {
      runs: runNumber,
      lorK: type,
      rate: sodo.rate || 0,
      amount: sodo.amount || 0,
    };
    
    // Calculate run-wise amounts for this soda
    const sodaRunWiseAmounts = calculateRunWiseWinLossForSoda(sodaData);
    
    // For Session games, calculate commission once per soda
    let totalCommission = 0;
    if (isSessionGame) {
      const partyCommission = calculateSessionPartyCommission(sodo);
      const thirdPartyCommission = calculateSessionThirdPartyCommission(sodo);
      
      // If soda is a partner soda, add commission; otherwise subtract commission
      if (sodo.isPartnerSoda) {
        totalCommission = -(partyCommission + thirdPartyCommission);
      } else {
        totalCommission = -(partyCommission + thirdPartyCommission);
      }
    }
    
    // Accumulate amounts for each run, deducting commission for Session games
    for (let run = 0; run <= MAX_RUN_FOR_SESSION; run++) {
      const currentTotal = totalRunWiseAmounts.get(run) || 0;
      const sodaAmount = sodaRunWiseAmounts.get(run) || 0;
      // For Session games, deduct commission from each run-wise amount
      let finalAmount = 0;
      
      if(sodo.isPartnerSoda) {
        finalAmount = Number((sodaAmount - totalCommission).toFixed(4));
      } else {
        finalAmount = Number((sodaAmount + totalCommission).toFixed(4));
      }
      
      totalRunWiseAmounts.set(run, currentTotal + finalAmount);
    }
  }
  
  return totalRunWiseAmounts;
};

/**
 * Session: signed win/loss for one soda at a single match run, in the same display scale as the
 * Session game board run list (`RunsAmountList` shows `raw / 100`).
 *
 * SessionGameBoard aggregates with `calculateTotalRunWiseWinLoss(allSodas, true)`; this is the
 * per-soda equivalent: `calculateTotalRunWiseWinLoss([sodo], true).get(matchRun) / 100`.
 */
export const getSessionSodaWinLossDisplayAtRun = (sodo: Sodo, matchRun: number): number => {
  const map = calculateTotalRunWiseWinLoss([sodo], true);
  const r = Math.min(MAX_RUN_FOR_SESSION, Math.max(0, Math.floor(Number(matchRun))));
  const raw = map.get(r) ?? 0;
  return raw / 100;
};